import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { verifyUserMobileLogin, firstTimeLogin } from "../../../api/api";
import Cookies from "js-cookie";
import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Fade,
    Button,
    Alert,
} from "@mui/material";
import { Person as PersonIcon, CheckCircle as CheckIcon, Error as ErrorIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Styled components
const BackgroundBox = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: theme.spacing(2),
    boxSizing: 'border-box',
    width: '100%',
    overflowX: 'hidden',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5),
    },
    [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1),
    },
}));

const LoadingCard = styled(Box)(({ theme }) => ({
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 24,
    padding: '48px 64px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
    maxWidth: 500,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    wordWrap: 'break-word',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
        borderRadius: 16,
        maxWidth: '100%',
    },
    [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(2),
        borderRadius: 12,
        maxWidth: '100%',
    },
}));

const SuccessIcon = styled(Box)(({ theme }) => ({
    width: 80,
    height: 80,
    borderRadius: '50%',
    backgroundColor: theme.palette.success.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    animation: 'scaleIn 0.5s ease-out',
    [theme.breakpoints.down('sm')]: {
        width: 64,
        height: 64,
        marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.down('xs')]: {
        width: 56,
        height: 56,
        marginBottom: theme.spacing(1.5),
    },
    '@keyframes scaleIn': {
        '0%': {
            transform: 'scale(0)',
            opacity: 0,
        },
        '100%': {
            transform: 'scale(1)',
            opacity: 1,
        },
    },
}));

const SpinnerBox = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'inline-block',
    marginBottom: 24,
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
    },
}));

const ErrorIconBox = styled(Box)(({ theme }) => ({
    width: 80,
    height: 80,
    borderRadius: '50%',
    backgroundColor: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    animation: 'scaleIn 0.5s ease-out',
    [theme.breakpoints.down('sm')]: {
        width: 64,
        height: 64,
        marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.down('xs')]: {
        width: 56,
        height: 56,
        marginBottom: theme.spacing(1.5),
    },
}));

const VerifyMobile = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
    const [message, setMessage] = useState('');
    const [errorDetails, setErrorDetails] = useState('');
    const [nextSteps, setNextSteps] = useState([]);
    const [nextAction, setNextAction] = useState(null);

    useEffect(() => {
        const verifyLogin = async () => {
            const queryParams = new URLSearchParams(window.location.search);
            const user = queryParams.get("user");

            if (!user) {
                setStatus('error');
                setMessage('Ugyldig innlogging');
                setErrorDetails('Vi kunne ikke verifisere din identitet. Dette kan skje hvis innloggingsprosessen ble avbrutt.');
                setNextSteps([
                    'Gå tilbake til innloggingssiden',
                    'Prøv å logge inn på nytt',
                    'Kontakt support hvis problemet vedvarer'
                ]);
                return;
            }

            try {
                const { data } = await verifyUserMobileLogin(user);

                if (!data.status) {
                    const errorMsg = data.message || 'Innlogging mislyktes';
                    
                    // Check if user exists but has no company associations (cancelled campaign registration)
                    // In this case, redirect them to register via campaign
                    if (errorMsg.includes('not found') || errorMsg.includes('Please register')) {
                        // User account exists but has no company associations
                        // This happens when user cancelled campaign registration
                        // Redirect to login page with message to register via campaign
                        setStatus('error');
                        setMessage('Ingen tilknytning til bedrift');
                        setErrorDetails('Din konto eksisterer, men du har ingen tilknytning til en bedrift. Vennligst registrer deg via en kampanje for å få tilgang.');
                        setNextSteps([
                            'Gå tilbake til innloggingssiden',
                            'Registrer deg via en kampanje',
                            'Eller kontakt support for hjelp'
                        ]);
                        return;
                    }
                    
                    setStatus('error');
                    setMessage(errorMsg);
                    
                    if (errorMsg.includes('ikke funnet') || errorMsg.includes('not found')) {
                        setErrorDetails('Vi kunne ikke finne en bruker med dette telefonnummeret. Du må være registrert på forhånd for å få tilgang.');
                        setNextSteps([
                            'Kontroller at telefonnummeret er riktig',
                            'Kontakt oss for å bli registrert',
                            'Prøv å logge inn på nytt'
                        ]);
                    } else if (errorMsg.includes('slettet') || errorMsg.includes('deleted')) {
                        setErrorDetails('Din konto har blitt deaktivert. Kontakt support for hjelp.');
                        setNextSteps([
                            'Kontakt support for å reaktivere kontoen',
                            'Prøv å logge inn på nytt senere'
                        ]);
                    } else {
                        setErrorDetails('Vi kunne ikke fullføre innloggingen. Dette kan skyldes en midlertidig feil.');
                        setNextSteps([
                            'Prøv å logge inn på nytt',
                            'Kontroller internettforbindelsen din',
                            'Kontakt support hvis problemet vedvarer'
                        ]);
                    }
                    return;
                }

                setStatus('success');
                setMessage('Innlogging vellykket! Forbereder...');

                // Set cookies immediately
                const userId = data.user.id;
                const companies = data?.companies || [];
                const companyId = companies[0]?.id;
                
                // Validate we have required data
                if (!userId) {
                    console.error("User ID not found in response");
                    setStatus('error');
                    setMessage('Innlogging mislyktes: Bruker-ID mangler.');
                    return;
                }
                
                if (!companyId && companies.length === 0) {
                    console.error("No company found for user");
                    setStatus('error');
                    setMessage('Innlogging mislyktes: Ingen bedrift tilknyttet brukeren.');
                    return;
                }
                
                // Set cookies - try multiple times if needed
                const setCookiesWithRetry = () => {
                    try {
                        Cookies.set("user_id", String(userId), {
                            expires: 90,
                            path: '/',
                            sameSite: 'lax'
                        });
                        
                        if (companyId) {
                            Cookies.set("company_id", String(companyId), {
                                expires: 90,
                                path: '/',
                                sameSite: 'lax'
                            });
                        }
                        
                        // Verify cookies were set
                        const checkUserId = Cookies.get("user_id");
                        const checkCompId = companyId ? Cookies.get("company_id") : null;
                        
                        console.log("Cookies set - user_id:", checkUserId, "company_id:", checkCompId);
                        
                        return checkUserId && (companyId ? checkCompId : true);
                    } catch (err) {
                        console.error("Error setting cookies:", err);
                        return false;
                    }
                };
                
                // Set cookies immediately
                if (!setCookiesWithRetry()) {
                    console.warn("First cookie set attempt failed, retrying...");
                    // Small delay and retry
                    setTimeout(() => {
                        if (!setCookiesWithRetry()) {
                            console.error("Failed to set cookies after retry");
                            // Still proceed - cookies might work on navigation
                        }
                    }, 100);
                }

                try {
                    const firsttimeCheck = await firstTimeLogin({
                        user: data.user.mobile_no || user,
                    });

                    const isFirstTime = firsttimeCheck?.data?.first_time === true;
                    const hasMultipleCompanies = companies.length > 1;

                    // Set next action for display
                    if (isFirstTime) {
                        setNextAction('Du blir videresendt til aksept-siden hvor du kan godta vilkårene og se dine kuponger.');
                    } else if (hasMultipleCompanies) {
                        setNextAction('Du kan velge hvilket selskap du vil se kuponger for.');
                    } else {
                        setNextAction('Du blir videresendt til hjemmesiden hvor du kan se dine kuponger og poeng.');
                    }

                    setTimeout(() => {
                        // Ensure cookies are set one more time before navigation
                        setCookiesWithRetry();
                        
                        if (isFirstTime) {
                            navigate("/user_acceptance", {
                                state: { 
                                    mobile: data.user.mobile_no || user,
                                    company_id: companyId
                                },
                                replace: true
                            });
                        } else if (hasMultipleCompanies) {
                            navigate("/companies", {
                                state: { companies },
                                replace: true
                            });
                        } else {
                            navigate("/user_home", {
                                state: {
                                    mobile: data.user.mobile_no || user,
                                    company_id: companyId,
                                },
                                replace: true
                            });
                        }
                    }, 1500);
                } catch (firstTimeError) {
                    setStatus('error');
                    setMessage('Innlogging sjekk mislyktes');
                    setErrorDetails('Vi kunne ikke fullføre innloggingsprosessen. Dette kan skyldes en midlertidig feil.');
                    setNextSteps([
                        'Prøv å logge inn på nytt',
                        'Kontroller internettforbindelsen din',
                        'Kontakt support hvis problemet vedvarer'
                    ]);
                }
            } catch (error) {
                let errorMessage = 'Serverfeil';
                let errorDetailsText = '';
                let nextStepsList = [];

                // Check error type and provide helpful messages
                if (error.response?.status === 404) {
                    errorMessage = 'Bruker ikke funnet';
                    errorDetailsText = 'Vi kunne ikke finne en bruker med dette telefonnummeret. Du må være registrert på forhånd for å få tilgang.';
                    nextStepsList = [
                        'Kontroller at telefonnummeret er riktig',
                        'Kontakt oss for å bli registrert',
                        'Prøv å logge inn på nytt'
                    ];
                } else if (error.response?.status === 401 || error.response?.status === 403) {
                    errorMessage = 'Tilgang nektet';
                    errorDetailsText = 'Du har ikke tilgang til denne tjenesten. Dette kan skje hvis kontoen din er deaktivert.';
                    nextStepsList = [
                        'Kontakt support for hjelp',
                        'Prøv å logge inn på nytt senere'
                    ];
                } else if (error.response?.status >= 500) {
                    errorMessage = 'Serverfeil';
                    errorDetailsText = 'Vår server opplever tekniske problemer. Dette er vanligvis midlertidig.';
                    nextStepsList = [
                        'Vent noen minutter og prøv igjen',
                        'Sjekk internettforbindelsen din',
                        'Kontakt support hvis problemet vedvarer'
                    ];
                } else if (!error.response) {
                    errorMessage = 'Ingen tilkobling';
                    errorDetailsText = 'Vi kunne ikke koble til serveren. Dette kan skyldes nettverksproblemer.';
                    nextStepsList = [
                        'Sjekk internettforbindelsen din',
                        'Prøv å oppdatere siden',
                        'Kontroller at du har internettilgang'
                    ];
                } else {
                    errorMessage = 'Innlogging mislyktes';
                    errorDetailsText = 'En uventet feil oppstod. Dette kan skyldes en midlertidig feil.';
                    nextStepsList = [
                        'Prøv å logge inn på nytt',
                        'Kontroller internettforbindelsen din',
                        'Kontakt support hvis problemet vedvarer'
                    ];
                }

                setStatus('error');
                setMessage(errorMessage);
                setErrorDetails(errorDetailsText);
                setNextSteps(nextStepsList);
            }
        };

        verifyLogin();
    }, [navigate]);

    return (
            <BackgroundBox>
                <Container 
                    maxWidth="sm"
                    disableGutters
                    sx={{
                        width: '100%',
                        maxWidth: { xs: '100%', sm: 600 },
                        px: { xs: 0.5, sm: 2 },
                    }}
                >
                    <Fade in={true} timeout={500}>
                        <LoadingCard>
                            {status === 'loading' && (
                                <>
                                    <SpinnerBox>
                                        <CircularProgress 
                                            thickness={4}
                                            sx={{
                                                color: 'primary.main',
                                                width: { xs: '56px !important', sm: '64px !important', md: '80px !important' },
                                                height: { xs: '56px !important', sm: '64px !important', md: '80px !important' },
                                            }}
                                        />
                                    </SpinnerBox>
                                    <Typography 
                                        variant="h5" 
                                        gutterBottom 
                                        sx={{ 
                                            fontWeight: 600, 
                                            color: 'text.primary', 
                                            mb: 2,
                                            fontSize: { xs: '1.1rem', sm: '1.5rem' },
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            px: { xs: 1, sm: 0 },
                                        }}
                                    >
                                        Verifiserer innlogging...
                                    </Typography>
                                    <Typography 
                                        variant="body1" 
                                        sx={{ 
                                            color: 'text.secondary',
                                            fontSize: { xs: '0.875rem', sm: '1rem' },
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            px: { xs: 1, sm: 0 },
                                        }}
                                    >
                                        Vennligst vent mens vi verifiserer din identitet
                                    </Typography>
                                </>
                            )}

                            {status === 'success' && (
                                <>
                                    <SuccessIcon>
                                        <CheckIcon sx={{ 
                                            fontSize: { xs: 36, sm: 48 }, 
                                            color: 'white' 
                                        }} />
                                    </SuccessIcon>
                                    <Typography 
                                        variant="h5" 
                                        gutterBottom 
                                        sx={{ 
                                            fontWeight: 600, 
                                            color: 'text.primary', 
                                            mb: 2,
                                            fontSize: { xs: '1.1rem', sm: '1.5rem' },
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            px: { xs: 1, sm: 0 },
                                        }}
                                    >
                                        Innlogging vellykket!
                                    </Typography>
                                    <Typography 
                                        variant="body1" 
                                        sx={{ 
                                            color: 'text.secondary', 
                                            mb: 2,
                                            fontSize: { xs: '0.875rem', sm: '1rem' },
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            px: { xs: 1, sm: 0 },
                                        }}
                                    >
                                        {message}
                                    </Typography>
                                    
                                    {nextAction && (
                                        <Box sx={{ 
                                            mt: { xs: 2, sm: 3 }, 
                                            p: { xs: 1.5, sm: 2 }, 
                                            bgcolor: 'info.light', 
                                            borderRadius: 2,
                                            textAlign: 'left',
                                            mx: { xs: 1, sm: 0 },
                                        }}>
                                            <Typography 
                                                variant="subtitle2" 
                                                sx={{ 
                                                    fontWeight: 600, 
                                                    mb: 1, 
                                                    color: 'info.dark',
                                                    fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                Hva skjer nå?
                                            </Typography>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: 'info.dark',
                                                    fontSize: { xs: '0.813rem', sm: '0.875rem' },
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'break-word',
                                                }}
                                            >
                                                {nextAction}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    <CircularProgress 
                                        thickness={4}
                                        sx={{ 
                                            mt: 2, 
                                            color: 'primary.main',
                                            width: { xs: '20px !important', sm: '24px !important' },
                                            height: { xs: '20px !important', sm: '24px !important' },
                                        }} 
                                    />
                                </>
                            )}

                            {status === 'error' && (
                                <>
                                    <ErrorIconBox>
                                        <ErrorIcon sx={{ 
                                            fontSize: { xs: 36, sm: 48 }, 
                                            color: 'white' 
                                        }} />
                                    </ErrorIconBox>
                                    <Typography 
                                        variant="h5" 
                                        gutterBottom 
                                        sx={{ 
                                            fontWeight: 600, 
                                            color: 'error.main', 
                                            mb: 2,
                                            fontSize: { xs: '1.1rem', sm: '1.5rem' },
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            px: { xs: 1, sm: 0 },
                                        }}
                                    >
                                        {message}
                                    </Typography>
                                    
                                    {errorDetails && (
                                        <Alert 
                                            severity="error" 
                                            sx={{ 
                                                mb: 2, 
                                                textAlign: 'left',
                                                fontSize: { xs: '0.813rem', sm: '0.875rem' },
                                                mx: { xs: 1, sm: 0 },
                                                '& .MuiAlert-message': {
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'break-word',
                                                },
                                            }}
                                        >
                                            {errorDetails}
                                        </Alert>
                                    )}

                                    {nextSteps.length > 0 && (
                                        <Box sx={{ 
                                            mb: { xs: 2, sm: 3 }, 
                                            textAlign: 'left',
                                            px: { xs: 1, sm: 0 },
                                        }}>
                                            <Typography 
                                                variant="subtitle2" 
                                                sx={{ 
                                                    fontWeight: 600, 
                                                    mb: 1, 
                                                    color: 'text.primary',
                                                    fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                Hva kan du gjøre?
                                            </Typography>
                                            <Box component="ul" sx={{ pl: { xs: 1.5, sm: 2 }, m: 0 }}>
                                                {nextSteps.map((step, index) => (
                                                    <Typography 
                                                        key={index}
                                                        component="li" 
                                                        variant="body2" 
                                                        sx={{ 
                                                            mb: 0.5, 
                                                            color: 'text.secondary',
                                                            fontSize: { xs: '0.813rem', sm: '0.875rem' },
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'break-word',
                                                        }}
                                                    >
                                                        {step}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: { xs: 1.5, sm: 2 }, 
                                        justifyContent: 'center', 
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        flexWrap: 'wrap', 
                                        mt: { xs: 2, sm: 3 },
                                        px: { xs: 1, sm: 0 },
                                    }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => window.location.reload()}
                                            sx={{ 
                                                width: { xs: '100%', sm: 'auto' },
                                                minWidth: { xs: 'unset', sm: 120 },
                                                py: { xs: 1.25, sm: 1 },
                                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                            }}
                                        >
                                            Prøv igjen
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => navigate("/")}
                                            sx={{ 
                                                width: { xs: '100%', sm: 'auto' },
                                                minWidth: { xs: 'unset', sm: 120 },
                                                py: { xs: 1.25, sm: 1 },
                                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                            }}
                                        >
                                            Gå tilbake
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </LoadingCard>
                    </Fade>
                </Container>
            </BackgroundBox>
    );
};

export default VerifyMobile;
